import Layout from "../components/Layout.tsx";
import { useState } from "react";
import { RiCheckLine } from "@remixicon/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/Table.tsx";
import { Button } from "../components/Button.tsx";
import { Text } from "../components/Text.tsx";
import AdministrationBadgeList from "../components/AdministrationBadgeList.tsx";
import { FieldGroup, Field, Label } from "../components/Fieldset.tsx";
import { toast } from "react-hot-toast";
import LoadingState from "../components/LoadingState.tsx";
import { useExactSystemAuthorizations } from "../hooks/data.ts";
import { useUser } from "../context/UserContext.tsx";
import { ExactOnlineAuthService } from "../services/ExactOnlineAuthService.ts";
import { useOAuthPopupCallback, openOAuthPopup } from "../hooks/useOAuthPopup";
import { parseUtcDate } from "../utils.ts";
import { isReadOnly } from "../config.ts";

const formatUpdatedAt = (value: string): string =>
  new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parseUtcDate(value));

const Authentication = () => {
  const { user } = useUser();
  const { systemAuthorizations, loading, refresh } = useExactSystemAuthorizations(true);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);
  const authService = new ExactOnlineAuthService();

  // If this window was opened as an OAuth popup, forward result and close
  useOAuthPopupCallback();

  const ownAuthorizations = systemAuthorizations.filter(
    (authorization) => authorization.ownerUserId === user?.id
  );
  const otherAuthorizations = systemAuthorizations.filter(
    (authorization) => authorization.ownerUserId !== user?.id
  );

  const runAuthenticationFlow = async (existingSystemAuthorizationId?: number) => {
    const id = existingSystemAuthorizationId ?? -1;
    setPendingId(id);
    setSuccessId(null);
    try {
      const response = await authService.getAuthorizationUrl("authenticatie", existingSystemAuthorizationId);

      if (!response.success) {
        toast.error(response.error || "Er is een fout opgetreden");
        return;
      }

      if (response.data && typeof response.data === "string") {
        toast(response.message || "Authenticatie vereist");
        try {
          const result = await openOAuthPopup(response.data);
          if (result.status === "success") {
            setSuccessId(id);
          } else {
            toast.error(result.message || "Er is een fout opgetreden");
          }
        } catch {
          toast.error("Authenticatie popup gesloten of geblokkeerd");
        }
      } else {
        setSuccessId(id);
      }
    } catch (error) {
      toast.error("Er is een onverwachte fout opgetreden");
      console.error("Authentication error:", error);
    } finally {
      setPendingId(null);
      await refresh();
    }
  };

  return (
    <Layout
      title="Authenticatie"
      description=""
      stepTitle="Exact Online authenticaties"
      showBackButton={false}
      showNextButton={false}
    >
      <FieldGroup>
        <div className="space-y-1">
          <Text>
            Beheer hier je Exact Online-authenticatie. Hier kun je een nieuwe authenticatie aanmaken
            of controleren - deze is geldig voor alle administraties.
          </Text>
          <Text className="text-amber-600 dark:text-amber-400">
            ⚠️ Belangrijk: geef bij voorkeur toegang tot "Alle huidige en toekomstige administraties". Dit kun
            je later nog aanpassen in Mijn Exact Online - Beveiligingscentrum - Machtigingen voor
            mijn apps.
          </Text>
        </div>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            <Field>
              <Label>Jouw authenticaties</Label>
              {ownAuthorizations.length === 0 ? (
                <div className="mt-2 flex flex-col items-center gap-3 rounded-lg border border-zinc-950/10 px-4 py-8 text-center text-sm text-zinc-500">
                  Je hebt nog geen Exact Online-authenticatie.
                  <Button
                    color="primary"
                    disabled={pendingId !== null || isReadOnly}
                    onClick={() => runAuthenticationFlow()}
                  >
                    {pendingId === -1 ? "Bezig..." : "Nieuwe authenticatie aanmaken"}
                  </Button>
                </div>
              ) : (
                <Table dense grid className="mt-2 rounded-lg border border-zinc-950/10" tableClassName="table-fixed">
                  <TableHead>
                    <TableRow>
                      <TableHeader className="w-1/4">Authenticatie</TableHeader>
                      <TableHeader className="w-1/4">Administraties</TableHeader>
                      <TableHeader className="w-1/4">Actieve koppelingen</TableHeader>
                      <TableHeader>Laatst gecontroleerd</TableHeader>
                      <TableHeader>Acties</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ownAuthorizations.map((authorization) => {
                      const label = authorization.label ?? `Exact authenticatie ${authorization.id}`;
                      return (
                        <TableRow key={authorization.id}>
                          <TableCell className="font-medium text-zinc-950">{label}</TableCell>
                          <TableCell>
                            <AdministrationBadgeList administrations={authorization.administrations} />
                          </TableCell>
                          <TableCell className="text-zinc-500">
                            <div className="flex flex-col gap-1">
                              {!authorization.connectionNames?.length ? (
                                <span className="text-zinc-400">Geen</span>
                              ) : (
                                authorization.connectionNames.map((connectionName, index) => (
                                  <span key={`${connectionName}-${index}`} className="whitespace-nowrap">
                                    {connectionName}
                                  </span>
                                ))
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-500 whitespace-nowrap">
                            {formatUpdatedAt(authorization.updatedAt)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                color="primary"
                                disabled={pendingId !== null || isReadOnly}
                                onClick={() => runAuthenticationFlow(authorization.id)}
                              >
                                {pendingId === authorization.id ? "Controleren..." : "Authenticatie controleren"}
                              </Button>
                              {successId === authorization.id && (
                                <RiCheckLine className="size-5 text-green-600" aria-label="Authenticatie geldig" />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Field>

            {otherAuthorizations.length > 0 && (
              <Field>
                <Label>Overige authenticaties</Label>
                <Table dense grid className="mt-2 rounded-lg border border-zinc-950/10" tableClassName="table-fixed">
                  <TableHead>
                    <TableRow>
                      <TableHeader className="w-1/4">Authenticatie</TableHeader>
                      <TableHeader className="w-1/4">Administraties</TableHeader>
                      <TableHeader className="w-1/4">Actieve koppelingen</TableHeader>
                      <TableHeader>Laatst gecontroleerd</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otherAuthorizations.map((authorization) => {
                      const label = authorization.label ?? `Exact authenticatie ${authorization.id}`;
                      return (
                        <TableRow key={authorization.id}>
                          <TableCell className="font-medium text-zinc-950">{label}</TableCell>
                          <TableCell>
                            <AdministrationBadgeList administrations={authorization.administrations} />
                          </TableCell>
                          <TableCell className="text-zinc-500">
                            <div className="flex flex-col gap-1">
                              {!authorization.connectionNames?.length ? (
                                <span className="text-zinc-400">Geen</span>
                              ) : (
                                authorization.connectionNames.map((connectionName, index) => (
                                  <span key={`${connectionName}-${index}`} className="whitespace-nowrap">
                                    {connectionName}
                                  </span>
                                ))
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-500 whitespace-nowrap">
                            {formatUpdatedAt(authorization.updatedAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Field>
            )}
          </>
        )}
      </FieldGroup>
    </Layout>
  );
};

export default Authentication;
